import { Bot } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLlmChatDrawer } from '@/components/llm-chat/llm-chat-drawer-context';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';

const sliceString = (str: string, length: number) => {
  if (str.length > length) {
    return str.slice(0, length) + '...';
  }

  return str;
};

export interface SiteHeaderProps {
  breadcrumbs?: {
    href: string;
    children: string;
  }[];
  newButton?: 'invoice' | 'order' | 'expense' | 'participant';
}

export function SiteHeader({ breadcrumbs }: SiteHeaderProps) {
  const { t } = useTranslation();
  const { openChat } = useLlmChatDrawer();

  return (
    <header className="flex h-12 w-full min-w-0 shrink-0 items-center gap-2 overflow-hidden border-b border-border px-4">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <SidebarTrigger className="-ml-1 shrink-0" />
        <Separator
          orientation="vertical"
          className="mr-2 shrink-0 data-[orientation=vertical]:h-4"
        />
        {breadcrumbs && (
          <Breadcrumb className="min-w-0">
            <BreadcrumbList>
              {breadcrumbs.map((breadcrumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                const isLink = breadcrumb.href && !isLast;

                return (
                  <React.Fragment key={index}>
                    <BreadcrumbItem role="presentation">
                      {!isLast && (
                        <BreadcrumbLink
                          href={isLink ? breadcrumb.href : undefined}
                        >
                          {typeof breadcrumb.children === 'string' &&
                            sliceString(breadcrumb.children, 32)}
                          {typeof breadcrumb.children !== 'string' &&
                            breadcrumb.children}
                        </BreadcrumbLink>
                      )}
                      {isLast && (
                        <BreadcrumbPage>{breadcrumb.children}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator />}
                  </React.Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0"
          aria-label={t('llmChat.openAssistant')}
          title={t('llmChat.openAssistant')}
          onClick={() => openChat()}
        >
          <Bot className="size-4" />
        </Button>
      </div>
    </header>
  );
}
